<script setup>
import { computed, reactive, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import menu from "@/menu.js";
import NavBar from "@/components/NavBar.vue";
import AsideMenu from "@/components/AsideMenu.vue";
import FooterBar from "@/components/FooterBar.vue";
import Overlay from "@/components/Overlay.vue";
import PromoBar from "@/components/PromoBar.vue";
import ToastHost from "@/components/notifications/ToastHost.vue"
import logsService from "@/services/logs-service";
import { initFlowbite } from 'flowbite'
import mixpanel from 'mixpanel-browser';
import { mdiEmailFastOutline, mdiSchoolOutline } from "@mdi/js";
import ModalBox from "@/components/ModalBox.vue";
import JbButton from "@/components/JbButton.vue";
import Icon from "@/components/Icon.vue";
import { useCrispChat, CRISP_REPLY_SLA_HOURS, installCrispBubbleGate, installCrispLauncher } from "@/composables/useCrispChat";
import SupportChatButton from "@/components/SupportChatButton.vue";
import { useSupportHours } from "@/composables/useSupportHours";
import TutorialQuickAsk from "@/components/tutorial/TutorialQuickAsk.vue";
import { tutorialLocationFor } from "@/components/tutorial/tutorial-steps";
import { sendTutorialFeedback } from "@/services/tutorial.service";
import { useVoteActivity } from "@/composables/useVoteActivity";
import { useAlerts } from "@/composables/useAlerts";
import { notify } from "@/composables/useToast";
import { oauthFailureNotice } from "@/utils/oauth-failure";

const store = useStore();
const route = useRoute();
const router = useRouter();

/* The one place the support-chat confirmation is rendered. Every screen that
   offers "chat to us" routes through this dialog before Crisp opens. */
const {
  isConfirmOpen: isCrispConfirmOpen,
  confirmCrispChat,
  cancelCrispChat,
  pendingMessage: crispPendingMessage,
  offerTutorial: crispOfferTutorial,
  setPendingMessage: setCrispPendingMessage
} = useCrispChat();

const crispReplySlaHours = CRISP_REPLY_SLA_HOURS;

/* Starts THE vote poller, for the whole app.
   Called here rather than in OrdersTable because that is the entire point: vote
   progress used to stop existing the moment you navigated off /orders, so the
   toasts only reached people already staring at the numbers they describe. This
   call fixes the poller's lifetime to the app's — it gates itself on login,
   halves its rate off the jobs page and stops dead while the tab is hidden, all
   of which it can only do from above the router. The return value is ignored
   here on purpose: the state it exposes is consumed by OrdersTable, which calls
   the same composable and gets this same instance. */
useVoteActivity();

/* Starts THE account-alerts store, for the same reason and in the same place.
   The bell lives in NavBar, but creating the singleton from there would tie its
   lifetime to a component the layout unmounts on full-screen pages (login,
   checkout), which would throw away the fetched list and the read state and
   re-fetch on the way back. Called here, it is created once and the bell is a
   pure consumer. The return value is ignored on purpose. */
useAlerts();

/* The dialog is the single gate in front of Crisp, so the opening hours belong
   here: someone starting a chat at 2 AM on a Sunday should see that before they
   type, not after waiting for a reply that isn't coming until Monday. Same
   composable the Support page reads, so the two can never disagree. */
const { supportHours } = useSupportHours();

// Dismissing via the backdrop or Escape must clear the pending message too.
const onCrispConfirmUpdate = (value) => {
  if (!value) cancelCrispChat();
};

/* "Get an instant answer first" inside the chat dialog (TutorialQuickAsk.vue):
   a question the tutorial already answers should not wait for a person. Only
   for signed-in customers (the assistant needs the session) and only when the
   caller did not open the chat about something specific (`offerTutorial`). */
const showQuickAsk = computed(() => isLoggedIn.value && crispOfferTutorial.value);
// Re-created on every open, so the last customer question never lingers.
const quickAskKey = ref(0);
let chatOpenedWith = null;
let quickAsked = null; // { question, id } — the last instant answer, if any
watch(isCrispConfirmOpen, (open) => {
  if (!open) return;
  quickAskKey.value += 1;
  chatOpenedWith = crispPendingMessage.value;
  quickAsked = null;
});

/* If they still start the chat, the agent sees the question they already
   typed, after whatever the entry point had prepared. */
const onQuickAsked = ({ question, id }) => {
  quickAsked = { question, id };
  setCrispPendingMessage([chatOpenedWith, `My question: ${question}`].filter(Boolean).join("\n\n"));
};

/* Crisp's own bubble starts conversations too; route new ones through the
   same dialog, for signed-in customers (useCrispChat.js). */
installCrispBubbleGate(() => isLoggedIn.value);

/* Starting the chat after an instant answer means it did not solve it — that
   rating is what tells us which tutorial answers fall short. */
const startCrispChat = () => {
  if (quickAsked?.id) sendTutorialFeedback(quickAsked.id, false).catch(() => {});
  confirmCrispChat();
};

const openTutorialFromChat = (step) => {
  cancelCrispChat();
  router.push(step ? { path: "/tutorial", query: { step } } : tutorialLocationFor(route.path));
};

store.commit("user", {
  name: "John Doe",
  email: "john@example.com",
  // avatar:
  // `https://api.dicebear.com/5.x/identicon/svg?seed=${store.auth..username.replace(/[^a-z0-9]+/i, '-')}`,
});

const isLoggedIn = computed(() => store.state.auth.status.loggedIn);

/* While signed in, Crisp's bubble is hidden altogether in favour of the
   panel's own help button (SupportChatButton.vue), so a new conversation starts
   from the dialog and nothing flashes open. Signed out, Crisp is untouched. */
installCrispLauncher(() => isLoggedIn.value);

const email = computed(() => store?.state?.auth?.user?.user?.email)

const isAsideLgActive = computed(() => store.state.isAsideLgActive);
const isAsideMobileExpanded = computed(() => store.state.isAsideMobileExpanded);
const isAsideCollapsed = computed(() => store.state.isAsideCollapsed);

const darkMode = computed(() => store.state.darkMode)

/* The full-screen routes (login, register, forgot/reset password) draw their own
   layout: NavBar and AsideMenu are hidden there, so there is no sidebar to
   reserve room for. `.full-screen body { p-0 }` in _app.css says exactly that,
   but it CANNOT win — the padding below is an inline style, and an inline style
   beats any class. */
const isFullScreen = computed(() => store.state.isFullScreen)

// Sync body padding with sidebar collapsed state
const updateBodyPadding = () => {
  // Arabic flips the layout, so the space reserved for the sidebar has to move
  // to the other side with it.
  const isRtl = document.documentElement.getAttribute('dir') === 'rtl'
  document.body.style.paddingLeft = '0'
  document.body.style.paddingRight = '0'

  if (!isLoggedIn.value) return

  /* Reserving 15rem on a page with no sidebar indented the whole shell: the
     in-flow promo bar started 240px in, leaving a blank gutter down the left,
     and the auth screens' `fixed left-5` Back link — which is measured from the
     VIEWPORT, not the padding — landed in that gutter, beside the promo bar
     instead of below it. `.below-promo` then pushed it down by the bar's height
     as well, to clear a bar that was no longer above it. */
  if (isFullScreen.value) return

  const isXl = window.innerWidth >= 1280
  if (!isXl) return

  const width = isAsideCollapsed.value ? '4rem' : '15rem'
  document.body.style[isRtl ? 'paddingRight' : 'paddingLeft'] = width
}

// Logging in swaps the sidebar into existence without a resize or a collapse
// toggle, so the padding has to react to that too — otherwise the first page
// after login renders underneath the sidebar.
// isFullScreen flips on every navigation into or out of an auth screen, and a
// signed-in user can reach /login directly — so it has to be watched too.
watch([isAsideCollapsed, isLoggedIn, isFullScreen], updateBodyPadding)

onMounted(() => {
  updateBodyPadding()
  window.addEventListener('resize', updateBodyPadding)
})

const overlayClick = ()  => {
  store.dispatch("asideLgToggle", false);
  store.dispatch("asideMobileToggle", false);
};

const verified = computed(() => store?.state?.auth?.user?.user?.email_verified_at)

const userId = computed(() => store?.state?.auth?.user?.user?.id)

// Logout modal state
const logoutModal = computed(() => store.state.auth.logoutModal)

const onLogoutModalUpdate = (value) => {
  if (!value) store.dispatch("auth/hideLogoutModal");
};

const handleSessionExpiredLogin = () => {
  store.dispatch("auth/logout"); // idempotent, credentials already cleared
  store.dispatch("auth/hideLogoutModal");
  if (route.path !== "/login") router.push("/login");
};

/* ── Notices carried in the URL ─────────────────────────────────────────────
 *
 * This used to render ANY `?message=` text in a full-width banner styled as an
 * official notice, so a crafted link could make the panel say anything ("your
 * account is suspended, contact …") — and its transparent full-width wrapper
 * sat over the navbar and sidebar, swallowing their clicks until dismissed.
 *
 * Now the URL can only CHOOSE one of the notices below; the words are always
 * ours, and anything not listed is dropped without being shown.
 *
 * The one producer, found by searching backend-nextja, heleket and this app: a
 * failed Google/Discord sign-in (backend-nextja src/server/oauth.ts) redirects
 * with `error=auth_failed&reason=<code>&message=<sentence>`. With the default
 * OAUTH_CALLBACK_URL that lands INSIDE the hash (`#/auth/callback?…`), where
 * AuthCallback.vue owns it; only a callback URL configured without the hash
 * puts it in `location.search`, which is the one place read here — so a
 * failure is never reported twice. Its `message` is never rendered: `reason`
 * picks the copy. Nothing sends `type`, so each notice carries its own
 * severity rather than trusting the link for one.
 */
// Every parameter this reader owns — stripped from `location.search` once read.
const URL_NOTICE_PARAMS = ["message", "type", "error", "reason"];

// The copy lives in utils/oauth-failure.js, shared with AuthCallback.vue.
const urlNoticeFor = (params) =>
  params.get("error") === "auth_failed" ? oauthFailureNotice(params.get("reason")) : null;

const showUrlNotice = () => {
  try {
    const search = new URLSearchParams(window.location.search);

    const notice = urlNoticeFor(search);
    if (notice) notify({ ...notice, dedupeKey: "url-notice:oauth-failed" });

    /* Strip ours, keeping the hash and anyone else's parameters, without
       triggering the router. `history.state` is passed back rather than `{}`:
       vue-router keeps its own bookkeeping there. */
    if (URL_NOTICE_PARAMS.some((key) => search.has(key))) {
      URL_NOTICE_PARAMS.forEach((key) => search.delete(key));
      const rest = search.toString();
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash
      );
    }
  } catch (error) {
    console.error("Could not read the notice in the URL:", error);
  }
};

const OPTIONS = computed(() => window.location.href.includes("localhost") ? { debug: true } : {});

mixpanel.init('fc90981771d66b4bd2c59a82c3963cb2', { ...OPTIONS });

onMounted(() => {
  initFlowbite();
  showUrlNotice();
  if (userId?.value) {
    mixpanel.identify(userId.value)
  }
})

/* ── Skip link ──────────────────────────────────────────────────────────────
 * Lands on <main id="main-content">, which MainSection renders. The two
 * per-campaign views routed straight to a card (/logs/chart/:id,
 * /logs/grouped/:id) and the full-screen auth pages have no MainSection, so it
 * falls back to the routed page's own root element, then to its heading —
 * the link must never be a dead end.
 *
 * Not a hash link: on this hash router `#main-content` is a ROUTE, and would
 * navigate to it. Focus is moved by hand, with a temporary tabindex that is
 * dropped again on blur, so clicking inside the page never focuses <main>. */
const pageRef = ref(null);

const skipToContent = () => {
  const pageRoot = pageRef.value?.$el;
  const target =
    document.getElementById("main-content") ||
    (pageRoot && pageRoot.nodeType === Node.ELEMENT_NODE ? pageRoot : null) ||
    document.querySelector("#app h1");
  if (!target) return;
  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
    target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  }
  target.focus();
};

const configuredMenu = computed(() => {
  if (verified.value) {
    // Filter out "Verify" items when user is verified
    return menu.filter(item => {
      if (typeof item === 'string') {
        return item !== 'Verify';
      } else if (Array.isArray(item) && item.length > 0 && item[0].to === '/verify') {
        return false;
      }
      return true;
    });
  }
  return menu
})

onMounted(async() => {
  if (isLoggedIn.value) {
    // Telemetry only — a failure here must not become an unhandled rejection.
    await logsService.lastUsed(email.value).catch(() => {})
  }
})

</script>

<template>

  <!-- First focusable element on every page, invisible until focused: one Tab
       from the address bar, Enter, and the header and sidebar are skipped.
       `z-50` clears the docked sidebar (z-40) it would otherwise sit under. -->
  <a
    href="#main-content"
    class="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-card
           focus:bg-white focus:text-sm focus:font-semibold focus:text-brand-700
           focus:outline-none focus:ring-2 focus:ring-brand-500
           dark:focus:bg-gray-800 dark:focus:text-brand-300"
    @click.prevent="skipToContent"
  >
    Skip to content
  </a>

  <!-- The single host for every notification in the app. Stacked just under
       the navbar — see the LAYERING note in ToastHost.vue. -->
  <toast-host />
  <support-chat-button v-if="isLoggedIn" />

  <nav-bar v-if="isLoggedIn" />
  <aside-menu v-if="isLoggedIn" :menu="configuredMenu" />

  <!-- Above router-view, and outside the `isLoggedIn` guards above it, because
       the promo has to reach logged-out visitors on the pricing and register
       pages too — those are the ones it is actually trying to convert. -->
  <promo-bar />

  <!-- The slot only hands the skip link the routed page's root element;
       rendering is exactly what a bare <router-view /> did. -->
  <router-view v-slot="{ Component }">
    <component :is="Component" v-if="Component" ref="pageRef" />
  </router-view>
  <footer-bar />
  <!-- `z-[33]`: over the navbar (`z-[32]`), which it has always dimmed, and
       under the drawer (`z-40`). -->
  <overlay v-show="isAsideLgActive || isAsideMobileExpanded" z-index="z-[33]" @overlay-click="overlayClick" />

  <!-- The one host Google Translate builds its hidden widget into
       (google-translate.js targets this id; _translate-rtl.css hides it
       visually). Here, not inside LanguageSwitcher: Google builds nothing into
       a container under `display: none`, and on phones the header switcher is
       exactly that — so every language change there fell back to a full
       reload. Rendered at the root it exists at every width and on every
       route, and no longer leaves with a page: a switcher that owned it (the
       sign-in screens each render one) took the built widget with it when the
       page unmounted. -->
  <div id="google_translate_element" class="notranslate" translate="no" aria-hidden="true" />

  <!-- Session-expired logout modal -->
  <teleport to="body">
    <modal-box
      :model-value="logoutModal.show"
      :dismissable="false"
      @update:model-value="onLogoutModalUpdate"
    >
      <div class="py-2 text-center">
        <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 dark:bg-opacity-40">
          <svg class="h-7 w-7 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 class="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ logoutModal.title }}
        </h3>
        <p class="mt-2 mx-auto max-w-sm text-sm text-gray-600 dark:text-gray-400">
          {{ logoutModal.message }}
        </p>
        <jb-button
          v-if="logoutModal.showLogoutButton"
          color="danger"
          label="Log in again"
          class="mt-6 w-full max-w-sm"
          @click="handleSessionExpiredLogin"
        />
      </div>
    </modal-box>
  </teleport>

  <!-- Support-chat confirmation.
       Rendered once here, at the root, because `useCrispChat()` is called from
       seven different screens — putting the warning at each call site would
       guarantee one gets missed.

       No <teleport> wrapper: ModalBox teleports to <body> itself, and nesting a
       second teleport onto the same target stops prop updates reaching it — the
       dialog opens but then can never be closed. -->
    <modal-box
      :model-value="isCrispConfirmOpen"
      @update:model-value="onCrispConfirmUpdate"
    >
      <!-- Kept spare on purpose: a title with the open/closed badge, one
           muted line on reply time and today's hours, an optional instant
           answer, and the buttons. -->
      <div class="py-1">
        <div class="flex flex-wrap items-center gap-2 pr-8">
          <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">
            Start a live chat with support?
          </h3>
          <!-- Whether anyone is actually there right now, before they type. -->
          <span
            class="badge gap-1.5"
            :class="supportHours.isOpen
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-40 dark:text-green-300'
              : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'"
          >
            <span
              class="h-1.5 w-1.5 rounded-full"
              :class="supportHours.isOpen ? 'bg-green-500' : 'bg-gray-400'"
            />
            {{ supportHours.badge }}
          </span>
        </div>
        <!-- Reply time and opening hours in one line, in the reader's own clock. -->
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          A person replies within {{ crispReplySlaHours }} hours, usually much sooner.
          Weekdays {{ supportHours.range }}.
          <span :class="supportHours.isOpen ? 'text-green-700 dark:text-green-400' : ''">{{ supportHours.detail }}</span>
        </p>

        <tutorial-quick-ask
          v-if="showQuickAsk && isCrispConfirmOpen"
          :key="quickAskKey"
          class="mt-4"
          @asked="onQuickAsked"
          @resolved="cancelCrispChat"
          @open-step="openTutorialFromChat"
        />

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <!-- The guide for the page they are on, with screenshots. -->
          <button
            v-if="isLoggedIn"
            type="button"
            class="link inline-flex items-center justify-center gap-1.5 text-sm font-medium sm:mr-auto"
            @click="openTutorialFromChat()"
          >
            <icon :path="mdiSchoolOutline" :size="16" w="w-4" h="h-4" />
            Tutorial
          </button>
          <jb-button color="white" label="Cancel" @click="cancelCrispChat" />
          <jb-button color="info" label="Start chat" @click="startCrispChat" />
        </div>
      </div>
    </modal-box>
</template>

